import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ItemFunctionality } from 'angular-to-phaser';
import { NestableAuthenticatedView } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { Friendship, GameStateResponse, InventoryItem, ItemTypeConfig, ItemUse, PetDTO, Shop, ShopItem } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { InboxService } from 'src/app/services/inbox.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { PetService } from 'src/app/services/pet.service';
import { ShopService } from 'src/app/services/shop.service';

@Component({
    selector: 'app-inventory-item-options',
    templateUrl: './inventory-item-options.component.html',
    styleUrls: ['./inventory-item-options.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InventoryItemOptionsComponent extends NestableAuthenticatedView implements OnInit {

    @Input() item: InventoryItem | ShopItem
    @Input() containingShop: Shop;
    @Input() stackInstances: InventoryItem[] = [];

    friendsSignal: Signal<Friendship[]>;
    shopsSignal: Signal<Shop[]>;
    activePetsSignal: Signal<PetDTO[]>;

    selectedFriend: string;
    selectedShop: number;
    selectedPet: PetDTO;
    // Set inside showCritterStateUpdate()/showItemUseError(), both invoked from useItem()/
    // useItemOnPet()'s async subscribe (kicked off by a click, but resolving after that click's
    // own OnPush check already ran) - needs to be a signal for the result view to actually appear.
    protected itemUsedResult = signal<GameStateResponse>(undefined);

    selectedPetOption: { pet: PetDTO; use: ItemUse };
    selectedNonPetOption: ItemUse;

    quantityToAct = 1;

    get maxQuantity(): number {
        return this.stackInstances?.length || 1;
    }

    private instancesToActOn(): InventoryItem[] {
        const n = Math.min(Math.max(1, Math.floor(this.quantityToAct) || 1), this.maxQuantity);
        const pool = this.stackInstances?.length ? this.stackInstances : [this.item as InventoryItem];
        return pool.slice(0, n);
    }

    constructor(private activeModal: NgbActiveModal, private inventoryService: InventoryService, private inboxService: InboxService, private shopService: ShopService, private petService: PetService, route: ActivatedRoute, router: Router, private coreAppService: ApplicationService) {
        super(router, route, null, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
        this.friendsSignal = this.inboxService.friendsListSignal;
        this.activePetsSignal = this.petService.activeUserPetsSignal;
        this.shopsSignal = this.shopService.activeUserShopsSignal;
    }


    ngOnInit(): void {
        console.log("organge")
    }


    selectFriend(event: any) {
        this.selectedFriend = event.target.value;
    }

    sendToFriend(friend: string, item: InventoryItem) {
        this.subscribe(this.inventoryService.giftItems(this.instancesToActOn(), friend),
            () => this.coreAppService.modalService.dismissAll());
        return true;
    }

    private retrieveItemUsesFromRemote(itc: ItemTypeConfig) {
        this.subscribe(this.inventoryService.getItemUses(itc), x => {
            const updated = [...this.itemUsesState()];
            updated[itc.Id] = x;
            this.itemUsesState.set(updated);
        });
    }

    // Called with a per-row ItemTypeConfig argument from the template, not a bare field read - not
    // computed()-shaped (a zero-arg computed() has nothing to key memoization off a changing
    // argument by). itemUsesState is the genuinely mutable per-key cache; this stays a plain method
    // that lazily populates and reads through it, same shape as games-list's topScoresFor/hoverLeaderboards.
    public getItemUses(itc: ItemTypeConfig): ItemUse[] {
        if (this.containingShop) {
            return null;
        }
        const cached = this.itemUsesState();
        if (!cached[itc.Id]) {
            this.retrieveItemUsesFromRemote(itc);
        }
        return this.itemUsesState()[itc.Id];
    }

    public filterPetItemUses(itemUses: ItemUse[]) {
        if (itemUses === null || itemUses.length === 0) { return null; }

        return itemUses.filter(i => i.ItemFunctionality === ItemFunctionality.FeedToPet || i.ItemFunctionality === ItemFunctionality.PlayWithPet || i.ItemFunctionality === ItemFunctionality.ApplyToPet);
    }


    public filterNonPetItemUses(itemUses: ItemUse[]) {
        if (itemUses === null || itemUses.length === 0) { return null; }

        return itemUses.filter(i => i.ItemFunctionality === ItemFunctionality.OpenContainer);
    }

    private readonly itemUsesState: WritableSignal<ItemUse[][]> = signal<ItemUse[][]>([]);

    public useItemOnPet(item: InventoryItem, selection: { pet: PetDTO; use: ItemUse } = null) {
        if (selection) { this.selectedPetOption = selection; }

        if (item && this.selectedPetOption && this.selectedPetOption.pet && this.selectedPetOption.use) {
            this.inventoryService.useItem(item, this.selectedPetOption.use.ItemFunctionality, this.selectedPetOption.pet.Id).subscribe({
                next: this.showCritterStateUpdate.bind(this),
                error: this.showItemUseError.bind(this)
            })
            return true;
        }
        return false;
    }

    buyItem(item: ShopItem) {
        if (this.containingShop) {
            this.activeModal?.close();
            this.subscribe(this.shopService.purchaseShopItem(item, this.containingShop), null);
        }
    }


    public useItem(item: InventoryItem, selection: ItemUse = null) {
        if (selection) { this.selectedNonPetOption = selection; }

        if (item && this.selectedNonPetOption) {
            this.inventoryService.useItem(item, this.selectedNonPetOption.ItemFunctionality, null).subscribe({
                next: this.showCritterStateUpdate.bind(this),
                error: this.showItemUseError.bind(this)
            });
            return true;
        }
        return false;
    }

    private showCritterStateUpdate(gsr: GameStateResponse) {
        if (gsr.PetChanges?.length || gsr.PlayerStateUpdate?.AddedItems?.length || gsr.PlayerStateUpdate?.CashChangedAmount) {
            this.itemUsedResult.set(gsr);
        } else {        //if(gsr.PlayerStateUpdate?.RemovedItems?.find(i => i.Id == this.item.Id)){
            this.activeModal.close();
        }
    }

    // Reuses the same "phase 2" result view a successful use shows (app-critter-state-update),
    // driven by its Messages array, instead of a separate error UI - the server's CritterException
    // message (e.g. "Deleto already has Gold Background!") is already written to be shown to the
    // player as-is.
    private showItemUseError(err: HttpErrorResponse) {
        const message = typeof err.error === 'string' ? err.error : 'Something went wrong, please try again!';
        this.itemUsedResult.set({ Messages: [message], PetChanges: [], PlayerStateUpdate: null, BackgroundPlayerStateUpdate: null } as GameStateResponse);
    }

    public selectShop(event: any) {
        this.selectedShop = parseInt(event.target.value);
    }

    public moveToShop(item: InventoryItem, shop: number): boolean {
        this.subscribe(this.inventoryService.moveItemsToShop(this.instancesToActOn(), shop),
            () => this.coreAppService.modalService.dismissAll());
        return true;
    }
}
